import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiSearch, FiMap, FiTarget, FiShield } = FiIcons;

const HowItWorks = () => {
  const steps = [
    {
      number: "01",
      title: "The Cognitive Audit",
      description: "We confidentially assess the specific EF deficits that addiction has exacerbated—Is it crippling impulsivity? Emotional dysregulation? An inability to plan?",
      icon: FiSearch,
      color: "from-accent-magenta to-accent-purple"
    },
    {
      number: "02", 
      title: "Your Sobriety Blueprint",
      description: "Get a customized EF therapy program targeting your unique cognitive needs and recovery goals. This is precision brain training designed for your specific addiction and neurological profile.",
      icon: FiMap,
      color: "from-accent-gold to-accent-yellow"
    },
    {
      number: "03",
      title: "Cognitive Skill Building Sessions",
      description: "Weekly 1-on-1 therapy that strengthens executive functions through targeted evidence-based exercises and real-life practice. Learn to recognize triggers, pause before acting, and choose recovery-aligned behaviors consistently.",
      icon: FiTarget,
      color: "from-green-500 to-emerald-500"
    },
    {
      number: "04",
      title: "Recovery Integration & Relapse Prevention",
      description: "We ensure these skills become your new automatic response, empowering you to build a self-directed life of recovery that is resilient, fulfilling, and sustainable.",
      icon: FiShield,
      color: "from-blue-500 to-cyan-500"
    }
  ];

  return (
    <section id="how-it-works" className="py-20 bg-gradient-to-br from-accent-light via-white to-accent-purple/10">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl lg:text-5xl font-playfair font-bold text-gray-900 mb-6">
            From Struggling to Thriving in Recovery{' '}
            <span className="bg-gradient-to-r from-accent-magenta to-accent-purple bg-clip-text text-transparent">
              in 4 Steps
            </span>
          </h2>
        </motion.div>

        <div className="space-y-16">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              viewport={{ once: true }}
              className={`grid lg:grid-cols-2 gap-12 items-center ${
                index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
              }`}
            >
              <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                <div className="flex items-center gap-4 mb-6">
                  <div className={`w-16 h-16 bg-gradient-to-r ${step.color} rounded-2xl flex items-center justify-center`}>
                    <SafeIcon icon={step.icon} className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-4xl font-playfair font-bold text-gray-300">
                    {step.number}
                  </div>
                </div>
                
                <h3 className="text-2xl lg:text-3xl font-playfair font-bold text-gray-900 mb-4">
                  {step.title}
                </h3>
                
                <p className="text-lg text-gray-600 leading-relaxed">
                  {step.description}
                </p>
              </div>

              <div className={index % 2 === 1 ? 'lg:col-start-1' : ''}>
                <div className="relative">
                  <div className={`w-full h-80 bg-gradient-to-br ${step.color} rounded-3xl flex items-center justify-center`}>
                    <SafeIcon icon={step.icon} className="w-24 h-24 text-white opacity-20" />
                  </div>
                  
                  {/* Step number overlay */}
                  <div className="absolute -top-4 -right-4 w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-lg">
                    <span className="text-2xl font-bold text-gray-900">{step.number}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;